import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALGROUPLABORATORYFINANCE_LOAD_BY_TRIAL_GROUP_LABORATORY_ID,
    ACTION_TRIALGROUPLABORATORYFINANCE_LOAD_BY_ID,
    ACTION_TRIALGROUPLABORATORYFINANCE_LOAD_FAILURE,
    ACTION_TRIALGROUPLABORATORYFINANCE_LOAD_SUCCESS,

    ACTION_TRIALGROUPLABORATORYFINANCE_SAVE,
    ACTION_TRIALGROUPLABORATORYFINANCE_SAVE_SUCCESS,
    ACTION_TRIALGROUPLABORATORYFINANCE_SAVE_FAILURE,

    ACTION_TRIALGROUPLABORATORYFINANCE_VALIDATE,
    ACTION_TRIALGROUPLABORATORYFINANCE_VALIDATE_SUCCESS,
    ACTION_TRIALGROUPLABORATORYFINANCE_VALIDATE_FAILURE,

    ACTION_TRIALGROUPLABORATORYFINANCE_CREATE,
    ACTION_TRIALGROUPLABORATORYFINANCE_CREATE_SUCCESS,
    ACTION_TRIALGROUPLABORATORYFINANCE_CREATE_FAILURE,

    ACTION_TRIALGROUPLABORATORYFINANCE_SYNC,
    ACTION_TRIALGROUPLABORATORYFINANCE_SYNC_SUCCESS,
    ACTION_TRIALGROUPLABORATORYFINANCE_SYNC_FAILURE,

    ACTION_TRIALGROUPLABORATORYFINANCE_CLEAR,
    ACTION_TRIALGROUPLABORATORYFINANCE_FILTER,

    TrialGroupLaboratoryFinanceAction
} from '../actions/trialGroupLaboratoryFinance';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialGroupLaboratoryFinanceState {
    formData: Dtos.TrialGroupLaboratoryFinance | Dtos.TrialGroupLaboratoryFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialGroupLaboratoryFinance[];
    formLength: number;
}

const initialState: ITrialGroupLaboratoryFinanceState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialGroupLaboratoryFinanceState, _isLoading: boolean): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialGroupLaboratoryFinanceState,
    formData: Dtos.TrialGroupLaboratoryFinance | Dtos.TrialGroupLaboratoryFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialGroupLaboratoryFinanceState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialGroupLaboratoryFinanceState, formFilter: IFormFilter): ITrialGroupLaboratoryFinanceState {

    const items: Dtos.TrialGroupLaboratoryFinance[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.accountNumber.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialGroupLaboratoryFinanceState, propertiesToClear?: string[]): ITrialGroupLaboratoryFinanceState {

    if (!propertiesToClear) {
        const newState: ITrialGroupLaboratoryFinanceState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialGroupLaboratoryFinanceState, _isSaving: boolean): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialGroupLaboratoryFinanceState,
    formData: Dtos.TrialGroupLaboratoryFinance | Dtos.TrialGroupLaboratoryFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialGroupLaboratoryFinanceState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: ITrialGroupLaboratoryFinanceState, _isValidating: boolean): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITrialGroupLaboratoryFinanceState,
    formData: Dtos.TrialGroupLaboratoryFinance | Dtos.TrialGroupLaboratoryFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITrialGroupLaboratoryFinanceState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialGroupLaboratoryFinanceState, _isCreating: boolean): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialGroupLaboratoryFinanceState,
    formData: Dtos.TrialGroupLaboratoryFinance,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialGroupLaboratoryFinanceState, responseStatus: Dtos.ResponseStatus): ITrialGroupLaboratoryFinanceState {
    const newState: ITrialGroupLaboratoryFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialGroupLaboratoryFinanceReducer: Reducer<ITrialGroupLaboratoryFinanceState> = (state: ITrialGroupLaboratoryFinanceState = initialState, action: TrialGroupLaboratoryFinanceAction) => {
    switch (action.type) {
        case ACTION_TRIALGROUPLABORATORYFINANCE_LOAD_BY_TRIAL_GROUP_LABORATORY_ID:
        case ACTION_TRIALGROUPLABORATORYFINANCE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALGROUPLABORATORYFINANCE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYFINANCE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYFINANCE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALGROUPLABORATORYFINANCE_SAVE:
            return saving(state, true);
        case ACTION_TRIALGROUPLABORATORYFINANCE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYFINANCE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYFINANCE_VALIDATE:
            return validating(state, true);
        case ACTION_TRIALGROUPLABORATORYFINANCE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYFINANCE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYFINANCE_CREATE:
            return creating(state, true);
        case ACTION_TRIALGROUPLABORATORYFINANCE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TRIALGROUPLABORATORYFINANCE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYFINANCE_FILTER:
            return filter(state, action.formFilter);
        case ACTION_TRIALGROUPLABORATORYFINANCE_SYNC:
            return loading(state, true);
        case ACTION_TRIALGROUPLABORATORYFINANCE_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TRIALGROUPLABORATORYFINANCE_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default TrialGroupLaboratoryFinanceReducer;